import {
  computed,
  inject,
  onBeforeUnmount,
  provide,
  ref,
  watch,
  type InjectionKey,
  type Ref,
} from 'vue'

export type OverlayLayerKind =
  'dropdown' | 'menu' | 'popover' | 'tooltip' | 'drawer' | 'modal' | 'media'

export interface OverlayStackEntry {
  id: string
  kind: OverlayLayerKind
  parentId: string | null
}

export interface OverlayStack {
  entries: Readonly<Ref<readonly OverlayStackEntry[]>>
  activate: (entry: OverlayStackEntry) => void
  deactivate: (id: string) => void
  orderOf: (id: string) => number
  isTopmostBlocking: (id: string) => boolean
}

/** Kinds that visually block the page and can legitimately claim an outside-press/Escape —
 * lightweight kinds (dropdown/menu/popover/tooltip) never get to suppress one of these. */
const BLOCKING_KINDS: ReadonlySet<OverlayLayerKind> = new Set(['modal', 'drawer', 'media'])

export function createOverlayStack(): OverlayStack {
  const entries = ref<OverlayStackEntry[]>([])

  function isDescendant(
    entry: OverlayStackEntry,
    ancestorId: string,
    candidates: readonly OverlayStackEntry[],
  ): boolean {
    const byId = new Map(candidates.map((candidate) => [candidate.id, candidate]))
    const visited = new Set<string>()
    let parentId = entry.parentId
    while (parentId && !visited.has(parentId)) {
      if (parentId === ancestorId) return true
      visited.add(parentId)
      parentId = byId.get(parentId)?.parentId ?? null
    }
    return false
  }

  function deactivate(id: string): void {
    const current = entries.value
    entries.value = current.filter((entry) => entry.id !== id && !isDescendant(entry, id, current))
  }

  function activate(entry: OverlayStackEntry): void {
    const current = entries.value
    const existingSubtree = current.filter(
      (candidate) => candidate.id === entry.id || isDescendant(candidate, entry.id, current),
    )
    const withoutSubtree = current.filter((candidate) => !existingSubtree.includes(candidate))
    const block = [entry, ...existingSubtree.filter((candidate) => candidate.id !== entry.id)]

    if (!entry.parentId) {
      entries.value = [...withoutSubtree, ...block]
      return
    }

    const parentIndex = withoutSubtree.findIndex((candidate) => candidate.id === entry.parentId)
    if (parentIndex < 0) {
      entries.value = [...withoutSubtree, ...block]
      return
    }

    let insertionIndex = parentIndex + 1
    while (
      insertionIndex < withoutSubtree.length &&
      isDescendant(withoutSubtree[insertionIndex]!, entry.parentId, withoutSubtree)
    ) {
      insertionIndex += 1
    }
    entries.value = [
      ...withoutSubtree.slice(0, insertionIndex),
      ...block,
      ...withoutSubtree.slice(insertionIndex),
    ]
  }

  function orderOf(id: string): number {
    return entries.value.findIndex((entry) => entry.id === id)
  }

  /** True unless a LATER-activated blocking overlay is still open — e.g. a confirm dialog
   * raised from inside a modal is a sibling top-level mount, not its Vue-tree descendant, so
   * Headless UI's own outside-press detection can't tell the modal underneath to stand down. */
  function isTopmostBlocking(id: string): boolean {
    const blocking = entries.value.filter((entry) => BLOCKING_KINDS.has(entry.kind))
    return blocking.length === 0 || blocking[blocking.length - 1]?.id === id
  }

  return { entries, activate, deactivate, orderOf, isTopmostBlocking }
}

const overlayStack = createOverlayStack()
interface OverlayParentContext {
  id: string
  active: Readonly<Ref<boolean>>
}

const overlayParentKey: InjectionKey<OverlayParentContext | null> = Symbol('overlay-parent')
let nextOverlayId = 0

export function useOverlayLayer(kind: OverlayLayerKind, active: Readonly<Ref<boolean>>) {
  const id = `ui-overlay-${++nextOverlayId}`
  const parent = inject(overlayParentKey, null)
  const parentId = parent?.id ?? null
  const effectiveActive = computed(() => active.value && (parent?.active.value ?? true))
  const order = computed(() => overlayStack.orderOf(id))
  const isTopmostBlocking = computed(() => overlayStack.isTopmostBlocking(id))

  provide(overlayParentKey, { id, active: effectiveActive })

  watch(
    effectiveActive,
    (isActive) => {
      if (isActive) overlayStack.activate({ id, kind, parentId })
      else overlayStack.deactivate(id)
    },
    { immediate: true, flush: 'sync' },
  )

  onBeforeUnmount(() => overlayStack.deactivate(id))

  return { id, order, parentId, isTopmostBlocking }
}
